// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';

import { buildArtefacts, summarise } from './core/artefacts';
import { spliceBytes } from './core/bytes';
import { decode } from './core/decode';
import { visibleText } from './core/visible';
import { CATEGORY_META } from './scan/catalogue';
import { DEFAULT_POLICY, effectiveAction, planEdits, type Policy, type Profile } from './scan/policy';
import { scanHtmlDocument } from './scan/htmlscan';
import { dominantScripts, fileKindFor, scan as scanText } from './scan/scan';
import type { Action, Finding } from './scan/types';
import { expectedCodePoints, verifyDecodedSequence, verifyUntouchedBytes } from './scan/verify';
import { UCD_VERSION } from './data/unicode';
import * as copy from './copy';
import { EventLog } from './eventlog';
import { installGlossary } from './glossary';
import {
  encodePasted,
  filesFromDataTransfer,
  readFiles,
  supportsFolderPicker,
  type Incoming,
} from './ingest';
import type { FileResult, RunResult } from './types';
import type { ScanRequest, ScanResponse } from './worker';
import {
  ACTION_LABELS,
  availableActions,
  buildLedger,
  groupChip,
  groupSubtitle,
  groupTitle,
  tierWord,
  type LedgerGroup,
} from './ui/ledger';
import { chooseLines, renderLine } from './ui/reveal';
import { createDrawer, createModalHost, el, formatCount, renderCopy, toast } from './ui/shell';

/* ── elements ───────────────────────────────────────────────────────────────────────────────── */

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const dropzone = $<HTMLDivElement>('dropzone');
const pasteInput = $<HTMLTextAreaElement>('paste-input');
const fileInput = $<HTMLInputElement>('file-input');
const folderInput = $<HTMLInputElement>('folder-input');
const inputNote = $<HTMLParagraphElement>('input-note');
const progress = $<HTMLDivElement>('progress');
const progressFill = $<HTMLSpanElement>('progress-fill');
const progressLabel = $<HTMLParagraphElement>('progress-label');
const runSummary = $<HTMLParagraphElement>('run-summary');
const messagePanel = $<HTMLElement>('message-panel');
const messageHeading = $<HTMLHeadingElement>('message-heading');
const messageNote = $<HTMLParagraphElement>('message-note');
const messageList = $<HTMLDivElement>('message-list');
const revealPanel = $<HTMLElement>('reveal-panel');
const revealFile = $<HTMLSelectElement>('reveal-file');
const revealDir = $<HTMLSelectElement>('reveal-dir');
const dirField = $<HTMLLabelElement>('dir-field');
const revealNote = $<HTMLParagraphElement>('reveal-note');
const revealBox = $<HTMLDivElement>('reveal');
const bidiPane = $<HTMLDivElement>('bidi-pane');
const bidiGrid = $<HTMLDivElement>('bidi-grid');
const ledgerPanel = $<HTMLElement>('ledger-panel');
const ledgerList = $<HTMLUListElement>('ledger');
const profileSelect = $<HTMLSelectElement>('profile');
const profileNote = $<HTMLParagraphElement>('profile-note');
const filterSeverity = $<HTMLSelectElement>('filter-severity');
const exportPanel = $<HTMLElement>('export-panel');
const exportNote = $<HTMLParagraphElement>('export-note');
const verifyNote = $<HTMLParagraphElement>('verify-note');
const errorBox = $<HTMLDivElement>('error');
const errorText = $<HTMLParagraphElement>('error-text');

const log = new EventLog($<HTMLOListElement>('drawer-list'));
const modal = createModalHost($<HTMLDivElement>('modal-host'));
createDrawer($<HTMLElement>('drawer'), $<HTMLButtonElement>('log-toggle'), $<HTMLButtonElement>('log-close'));

/* ── state ──────────────────────────────────────────────────────────────────────────────────── */

interface Loaded extends FileResult {
  bytes: Uint8Array;
  text: string;
}

let loaded: Loaded[] = [];
let skipped: { name: string; reason: string }[] = [];
let policy: Policy = { ...DEFAULT_POLICY, byCodePoint: new Map() };
let worker: Worker | null = null;
let cancelled = false;
let fromPaste = false;

function newWorker(): Worker {
  return new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
}

/* ── static copy ────────────────────────────────────────────────────────────────────────────── */

function paintCopy(): void {
  $<HTMLHeadingElement>('hero-title').textContent = copy.HERO_TITLE;
  $<HTMLParagraphElement>('hero-body').append(renderCopy(copy.HERO_BODY));
  $<HTMLElement>('trust-strong').textContent = copy.TRUST_BANNER_STRONG;
  $<HTMLElement>('trust-body').textContent = ` ${copy.TRUST_BANNER_BODY} `;
  $<HTMLParagraphElement>('honesty').textContent = copy.HONESTY;
  const limits = $<HTMLUListElement>('limits-list');
  for (const l of copy.LIMITS) limits.append(el('li', {}, [renderCopy(l)]));
  profileNote.textContent = copy.PROFILE_NOTES.balanced!;
}

function fillModalTemplates(): void {
  const how = document.querySelector<HTMLTemplateElement>('#tpl-how')!;
  const howBody = how.content.querySelector('#how-body')!;
  copy.HOW_STEPS.forEach((s, i) => {
    howBody.append(el('h3', {}, [`${i + 1} · ${s.title}`]));
    howBody.append(el('p', {}, [renderCopy(s.body)]));
  });

  const priv = document.querySelector<HTMLTemplateElement>('#tpl-privacy')!;
  const fill = (sel: string, items: readonly string[]): void => {
    const ul = priv.content.querySelector(sel)!;
    for (const t of items) ul.append(el('li', {}, [t]));
  };
  fill('#privacy-protected', copy.PROTECTED);
  fill('#privacy-not', copy.NOT_PROTECTED);
  fill('#privacy-trust', copy.TRUST_SURFACE);

  const lim = document.querySelector<HTMLTemplateElement>('#tpl-limits')!;
  lim.content.querySelector('#limits-honesty')!.textContent = copy.HONESTY;
  const ul = lim.content.querySelector('#limits-modal-list')!;
  for (const t of copy.LIMITS) ul.append(el('li', {}, [renderCopy(t)]));

  const about = document.querySelector<HTMLTemplateElement>('#tpl-about')!;
  const ab = about.content.querySelector('#about-body')!;
  for (const p of copy.ABOUT) ab.append(el('p', {}, [renderCopy(p)]));
  about.content.querySelector('#ucd-version')!.textContent = UCD_VERSION;
}

const MODAL_TITLES: Record<string, string> = {
  how: 'How it works',
  privacy: 'Privacy & threat model',
  limits: 'What this cannot tell you',
  about: 'About charsight',
  keys: 'Keyboard shortcuts',
};

function openModal(name: string): void {
  const tpl = document.querySelector<HTMLTemplateElement>(`#tpl-${name}`);
  if (!tpl) return;
  modal.show(MODAL_TITLES[name] ?? name, tpl.content.cloneNode(true));
}

/* ── ingest ─────────────────────────────────────────────────────────────────────────────────── */

function showError(message: string): void {
  errorText.textContent = message;
  errorBox.removeAttribute('hidden');
  log.emit('error', message);
}

function clearError(): void {
  errorBox.setAttribute('hidden', '');
}

function reset(): void {
  loaded = [];
  skipped = [];
  policy = { ...policy, byCodePoint: new Map() };
  for (const p of [messagePanel, revealPanel, ledgerPanel, exportPanel]) p.setAttribute('hidden', '');
  runSummary.setAttribute('hidden', '');
  clearError();
}

async function ingest(incoming: Incoming[]): Promise<void> {
  reset();
  cancelled = false;
  const readable = incoming.filter((f) => f.route !== 'skipped');
  skipped = incoming
    .filter((f) => f.route === 'skipped')
    .map((f) => ({ name: f.name, reason: f.skipReason ?? 'skipped' }));

  if (readable.length === 0) {
    showError(
      skipped.length
        ? `Nothing here could be read. ${skipped.length} ${skipped.length === 1 ? 'file was' : 'files were'} skipped — the reasons are in the event log.`
        : 'Nothing to read.',
    );
    for (const s of skipped) log.emit('warn', `skipped ${s.name} — ${s.reason}`);
    return;
  }

  $<HTMLButtonElement>('start-over').removeAttribute('hidden');
  progress.removeAttribute('hidden');
  progressFill.style.width = '0%';
  log.emit('info', `reading ${readable.length} file${readable.length === 1 ? '' : 's'}`);
  for (const s of skipped) log.emit('warn', `skipped ${s.name} — ${s.reason}`);

  worker?.terminate();
  worker = newWorker();
  const w = worker;

  let done = 0;
  const results: Loaded[] = [];

  const textFiles = readable.filter((f) => f.route !== 'pdf');
  const pdfFiles = readable.filter((f) => f.route === 'pdf');

  const tick = (label: string): void => {
    done++;
    progressFill.style.width = `${Math.round((done / readable.length) * 100)}%`;
    progressLabel.textContent = `${done} of ${readable.length} · ${label}`;
  };

  await new Promise<void>((resolve) => {
    if (textFiles.length === 0) {
      resolve();
      return;
    }
    let outstanding = textFiles.length;
    w.addEventListener('message', (ev: MessageEvent<ScanResponse>) => {
      if (cancelled) return;
      const r = ev.data;
      const src = textFiles.find((f) => f.name === r.name)!;
      if (r.error) {
        log.emit('error', `${r.name} — ${r.error}`);
      } else {
        const findings = [...r.findings];
        let htmlFindings;
        if (src.route === 'html') {
          try {
            // DOMParser exists only on the main thread, so the CSS pass runs here. The document is
            // inert and the user's stylesheet is parsed as text — it never touches this page.
            const doc = new DOMParser().parseFromString(r.text, 'text/html');
            htmlFindings = scanHtmlDocument(doc);
            if (htmlFindings.length) {
              log.emit('warn', `${r.name} — ${htmlFindings.length} element(s) with hidden text`);
            }
          } catch {
            log.emit('warn', `${r.name} — could not be parsed as HTML; read as plain text`);
          }
        }
        results.push({
          name: r.name,
          size: src.bytes.length,
          route: src.route,
          encoding: r.encoding as Loaded['encoding'],
          findings,
          htmlFindings,
          codePoints: r.codePoints,
          bytes: src.bytes,
          text: r.text,
        });
        log.emit(
          findings.some((f) => f.severity === 'payload') ? 'warn' : 'info',
          `${r.name} — ${r.encoding}, ${formatCount(r.codePoints)} characters, ${findings.length} finding(s)`,
        );
      }
      tick(r.name);
      if (--outstanding === 0) resolve();
    });
    for (const [i, f] of textFiles.entries()) {
      const req: ScanRequest = {
        id: i,
        name: f.name,
        bytes: f.bytes,
        fileKind: fileKindFor(f.name),
      };
      w.postMessage(req);
    }
  });

  for (const f of pdfFiles) {
    if (cancelled) break;
    try {
      const { scanPdf } = await import('./pdf');
      const res = await scanPdf(f.bytes);
      // The recovered invisible text is itself scanned for hidden code points — tag smuggling
      // inside a PDF's invisible layer is two hiding mechanisms stacked, and finding only the outer
      // one would report the payload as unreadable when it is right there.
      const inner: Finding[] = [];
      if (res.hiddenText) {
        const d = decode(new TextEncoder().encode(res.hiddenText));
        inner.push(
          ...scanText(d.units, {
            fileKind: 'prose',
            dominantScripts: dominantScripts(d.units),
          }).findings.filter((x) => x.severity === 'payload'),
        );
      }
      results.push({
        name: f.name,
        size: f.bytes.length,
        route: 'pdf',
        findings: inner,
        pdfRuns: res.runs,
        pages: res.pages,
        ocrLayer: res.ocrLayer,
        codePoints: 0,
        bytes: f.bytes,
        text: '',
      });
      log.emit(
        res.runs.length ? 'warn' : 'info',
        `${f.name} — ${res.pages} page(s), ${res.runs.length} invisible text run(s)${res.ocrLayer ? ' (looks like a scanned page with an OCR layer)' : ''}`,
      );
    } catch (err) {
      log.emit('error', `${f.name} — ${err instanceof Error ? err.message : String(err)}`);
      results.push({
        name: f.name,
        size: f.bytes.length,
        route: 'skipped',
        skipReason: 'could not be opened as a PDF',
        findings: [],
        codePoints: 0,
        bytes: f.bytes,
        text: '',
      });
    }
    tick(f.name);
  }

  progress.setAttribute('hidden', '');
  if (cancelled) {
    showError(
      `Cancelled after ${done} of ${readable.length} files. Nothing here is a complete result — run it again to finish.`,
    );
    return;
  }

  loaded = results.sort((a, b) => a.name.localeCompare(b.name));
  render();
}

/* ── rendering ──────────────────────────────────────────────────────────────────────────────── */

function allFindings(): { name: string; findings: Finding[] }[] {
  return loaded.map((f) => ({ name: f.name, findings: f.findings }));
}

function render(): void {
  const totals = summarise(loaded);
  const payloadCount = totals.payloads;
  const pdfRuns = loaded.reduce((n, f) => n + (f.pdfRuns?.filter((r) => !r.ocrLayer).length ?? 0), 0);
  const htmlHidden = loaded.reduce(
    (n, f) => n + (f.htmlFindings?.filter((h) => !h.routine).length ?? 0),
    0,
  );

  const bits = [
    `${loaded.length} file${loaded.length === 1 ? '' : 's'} read`,
    skipped.length ? `${skipped.length} skipped` : '',
    payloadCount ? `${payloadCount} hidden message${payloadCount === 1 ? '' : 's'}` : '',
    totals.suspicious ? `${totals.suspicious} to look at` : '',
    totals.notable ? `${totals.notable} worth knowing` : '',
    pdfRuns ? `${pdfRuns} invisible PDF text run${pdfRuns === 1 ? '' : 's'}` : '',
    htmlHidden ? `${htmlHidden} element${htmlHidden === 1 ? '' : 's'} hidden by CSS` : '',
  ].filter(Boolean);
  runSummary.textContent = bits.join(' · ');
  runSummary.removeAttribute('hidden');

  renderMessages();
  renderReveal();
  renderLedger();
  renderExport();

  // The result is the point of the page and it appears below the fold, so keyboard and screen
  // reader users are moved to it rather than left on the button they pressed.
  messageHeading.focus({ preventScroll: false });
}

function renderMessages(): void {
  messageList.replaceChildren();
  const payloads: { file: string; f: Finding }[] = [];
  for (const file of loaded) {
    for (const f of file.findings) if (f.severity === 'payload') payloads.push({ file: file.name, f });
  }
  const pdfHidden = loaded.flatMap((file) =>
    (file.pdfRuns ?? []).filter((r) => r.recoverable && r.text.trim()).map((r) => ({ file: file.name, r })),
  );
  const htmlHidden = loaded.flatMap((file) =>
    (file.htmlFindings ?? []).filter((h) => !h.routine).map((h) => ({ file: file.name, h })),
  );

  messagePanel.removeAttribute('hidden');

  if (payloads.length === 0 && pdfHidden.length === 0 && htmlHidden.length === 0) {
    messageNote.textContent =
      'Nothing here decodes to a hidden message. That is not the same as “this file is safe” — a file can carry perfectly visible instructions that are every bit as dangerous.';
    return;
  }
  messageNote.textContent =
    'Quoted in full, with every character that renders as nothing written out as a visible token — so this text is safe to copy into a ticket without carrying the payload with it.';

  for (const { file, f } of payloads) {
    const box = el('div', { class: 'payload' });
    box.append(
      el('div', { class: 'payload-where' }, [
        `${file} · line ${f.line}, column ${f.column} · ${f.runLength} code points · ${CATEGORY_META[f.category].label}`,
      ]),
      el('p', { class: 'payload-mech' }, [f.reason]),
    );
    if (f.decoded) box.append(el('div', { class: 'payload-quote' }, [visibleText(f.decoded)]));
    if (f.decodedHex) {
      box.append(el('div', { class: 'payload-hex' }, [f.decodedHex]));
    }
    messageList.append(box);
  }

  for (const { file, r } of pdfHidden) {
    const box = el('div', { class: r.ocrLayer ? 'payload payload-quiet' : 'payload' });
    box.append(
      el('div', { class: 'payload-where' }, [`${file} · page ${r.page} · ${r.glyphCount} glyphs`]),
      el('p', { class: 'payload-mech' }, [
        r.ocrLayer
          ? 'Drawn invisibly on a page that also paints an image — the signature of a scanned page with an OCR text layer, which is entirely normal. Shown because a payload can hide inside one too.'
          : `Drawn but not visible: ${r.reasons.join(', ')}.`,
      ]),
      el('div', { class: 'payload-quote' }, [visibleText(r.text)]),
    );
    messageList.append(box);
  }

  for (const { file, h } of htmlHidden) {
    const box = el('div', { class: 'payload' });
    box.append(
      el('div', { class: 'payload-where' }, [`${file} · ${h.where}`]),
      el('p', { class: 'payload-mech' }, [`Hidden by ${h.how}.`]),
      el('div', { class: 'payload-quote' }, [visibleText(h.text.slice(0, 2000))]),
    );
    messageList.append(box);
  }
}

function renderReveal(): void {
  const textFiles = loaded.filter((f) => f.text);
  if (textFiles.length === 0) {
    revealPanel.setAttribute('hidden', '');
    return;
  }
  revealPanel.removeAttribute('hidden');

  if (revealFile.options.length !== textFiles.length) {
    revealFile.replaceChildren();
    for (const f of textFiles) revealFile.append(el('option', { value: f.name }, [f.name]));
  }
  const current = textFiles.find((f) => f.name === revealFile.value) ?? textFiles[0]!;
  revealFile.value = current.name;

  const flaggedLines = new Set(current.findings.filter((f) => f.severity !== 'expected').map((f) => f.line));
  const { lines, truncated } = chooseLines(current.text, flaggedLines);

  revealBox.replaceChildren();
  for (const line of lines) {
    const row = el('div', { class: line.interesting ? 'rv-line rv-hit' : 'rv-line' });
    row.append(el('span', { class: 'rv-num' }, [String(line.number)]));
    const body = el('span', { class: 'rv-text' });
    body.append(renderLine(line.text));
    row.append(body);
    revealBox.append(row);
  }
  revealNote.textContent = truncated
    ? `Showing ${lines.length} lines around the findings. ${truncated} more line${truncated === 1 ? '' : 's'} with findings were not shown — the findings file has every one.`
    : `Showing ${lines.length} line${lines.length === 1 ? '' : 's'} around the findings. Hover or tap a marked character to see what it is.`;

  renderBidi(current);
}

function renderBidi(file: Loaded): void {
  const bidiLines = new Set(
    file.findings.filter((f) => f.category === 'bidi' && f.severity !== 'notable').map((f) => f.line),
  );
  if (bidiLines.size === 0) {
    bidiPane.setAttribute('hidden', '');
    dirField.setAttribute('hidden', '');
    return;
  }
  bidiPane.removeAttribute('hidden');
  dirField.removeAttribute('hidden');
  const all = file.text.split('\n');
  bidiGrid.replaceChildren();
  bidiGrid.append(
    el('div', { class: 'bidi-head' }, [`As your browser renders it (${revealDir.value})`]),
    el('div', { class: 'bidi-head' }, ['In the order a parser reads it']),
  );
  for (const n of [...bidiLines].sort((a, b) => a - b).slice(0, 40)) {
    const line = all[n - 1] ?? '';
    const left = el('div', { class: 'bidi-cell' });
    left.dir = revealDir.value as 'ltr' | 'rtl' | 'auto';
    left.textContent = line;
    // Substituting every control for a visible token BEFORE insertion is what makes the right-hand
    // column strict logical order: with no bidi control left in the string, no reordering can occur,
    // so the browser renders logical order for free and no algorithm is needed.
    const right = el('div', { class: 'bidi-cell bidi-logical' }, [visibleText(line)]);
    bidiGrid.append(left, right);
  }
}

function renderLedger(): void {
  const groups = buildLedger(allFindings(), (f) => effectiveAction(f, policy));
  const showAll = filterSeverity.value === 'all';
  const visible = groups.filter((g) => showAll || !g.legitimate);

  if (visible.length === 0) {
    ledgerPanel.setAttribute('hidden', '');
    return;
  }
  ledgerPanel.removeAttribute('hidden');
  ledgerList.replaceChildren();
  for (const g of visible) ledgerList.append(ledgerRow(g));
}

function ledgerRow(g: LedgerGroup): HTMLLIElement {
  const li = el('li', { class: `ledger-row tier-${g.tier}${g.legitimate ? ' is-legit' : ''}` });

  li.append(el('span', { class: 'tier-word' }, [tierWord(g)]));

  const head = el('div', { class: 'ledger-head' });
  head.append(el('span', { class: 'chip-cp' }, [groupChip(g)]));
  head.append(el('span', { class: 'ledger-title' }, [groupTitle(g)]));
  li.append(head);

  li.append(el('p', { class: 'ledger-sub' }, [groupSubtitle(g)]));
  li.append(el('p', { class: 'ledger-why' }, [g.occurrences[0]!.reason]));

  const where = g.occurrences
    .slice(0, 6)
    .map((f) => `line ${f.line}:${f.column}`)
    .join(', ');
  li.append(
    el('p', { class: 'ledger-where' }, [
      g.occurrences.length > 6 ? `${where}, and ${g.occurrences.length - 6} more` : where,
    ]),
  );

  const actions = availableActions(g);
  if (actions.length === 0) {
    li.append(el('p', { class: 'ledger-kept' }, ['Kept — charsight identified this as a legitimate use.']));
  } else {
    const wrap = el('div', { class: 'ledger-action' });
    const label = el('label', { class: 'sr-only', for: `act-${g.key}` }, [`Action for ${groupTitle(g)}`]);
    const sel = el('select', { id: `act-${g.key}` }) as HTMLSelectElement;
    for (const a of actions) {
      const o = el('option', { value: a }, [ACTION_LABELS[a]]) as HTMLOptionElement;
      if (a === g.action) o.selected = true;
      sel.append(o);
    }
    sel.addEventListener('change', () => {
      const a = sel.value as Action;
      if (g.cp >= 0) policy.byCodePoint.set(g.cp, a);
      else policy.overrides[g.category] = a;
      log.emit('info', `${groupTitle(g)} → ${ACTION_LABELS[a]}`);
      renderLedger();
      renderExport();
    });
    wrap.append(label, sel);
    li.append(wrap);
  }
  return li;
}

interface Prepared {
  cleaned: { name: string; bytes: Uint8Array }[];
  changed: number;
  problem?: string;
}

function prepareCleaned(): Prepared {
  const cleaned: { name: string; bytes: Uint8Array }[] = [];
  let changed = 0;
  for (const file of loaded) {
    if (!file.text) continue;
    const plan = planEdits(file.findings, policy, (file.encoding ?? 'utf-8') as 'utf-8');
    if (plan.edits.length === 0) continue;
    const out = spliceBytes(file.bytes, plan.edits);

    const untouched = verifyUntouchedBytes(file.bytes, out, plan.edits);
    if (!untouched.ok) return { cleaned: [], changed: 0, problem: `${file.name}: ${untouched.problem}` };

    const before = decode(file.bytes);
    const expected = expectedCodePoints(before.units, plan.edits, (b) =>
      decode(b).units.map((u) => u.cp),
    );
    const seq = verifyDecodedSequence(
      expected,
      decode(out).units.map((u) => u.cp),
    );
    if (!seq.ok) return { cleaned: [], changed: 0, problem: `${file.name}: ${seq.problem}` };

    cleaned.push({ name: file.name, bytes: out });
    changed += plan.applied.length;
  }
  return { cleaned, changed };
}

let prepared: Prepared = { cleaned: [], changed: 0 };

function renderExport(): void {
  if (loaded.length === 0) {
    exportPanel.setAttribute('hidden', '');
    return;
  }
  exportPanel.removeAttribute('hidden');
  prepared = prepareCleaned();

  if (prepared.problem) {
    verifyNote.textContent = `The cleaned file did not come out as promised, so it is not being offered: ${prepared.problem}`;
    verifyNote.className = 'field-note verify-bad';
    $<HTMLButtonElement>('dl-zip').disabled = true;
    return;
  }
  $<HTMLButtonElement>('dl-zip').disabled = false;
  verifyNote.className = 'field-note verify-ok';
  verifyNote.textContent = prepared.cleaned.length
    ? `Checked: every byte outside the ${prepared.changed} approved change${prepared.changed === 1 ? '' : 's'} is identical to your original, and the result decodes to exactly what the policy predicted.`
    : 'Nothing is being changed under this policy, so there are no cleaned files — only the report and the findings.';

  const pdfs = loaded.filter((f) => f.route === 'pdf').length;
  exportNote.textContent =
    `One zip with the findings file, the report, the policy and ${prepared.cleaned.length} cleaned file${prepared.cleaned.length === 1 ? '' : 's'}.` +
    (pdfs ? ' Charsight reads PDFs; it does not rewrite them, so no cleaned PDF is produced.' : '') +
    (fromPaste ? ` ${copy.PASTE_CAVEAT}` : '');

  $<HTMLButtonElement>('copy-cleaned').hidden = !(fromPaste && prepared.cleaned.length === 1);
}

/* ── artefacts ──────────────────────────────────────────────────────────────────────────────── */

function runResult(): RunResult {
  return {
    files: loaded.map((f) => ({ ...f })),
    at: new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC',
    skipped,
  };
}

function textMap(): Map<string, string> {
  return new Map(loaded.map((f) => [f.name, f.text]));
}

function policyJson(): string {
  return JSON.stringify(
    {
      tool: 'charsight',
      ucdVersion: UCD_VERSION,
      profile: policy.profile,
      byCodePoint: Object.fromEntries(
        [...policy.byCodePoint].map(([cp, a]) => [`U+${cp.toString(16).toUpperCase().padStart(4, '0')}`, a]),
      ),
      byCategory: policy.overrides,
    },
    null,
    2,
  );
}

function download(name: string, data: BlobPart, type: string): void {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function artefacts() {
  return buildArtefacts(runResult(), textMap(), prepared.cleaned, policyJson(), policy.profile);
}

/* ── wiring ─────────────────────────────────────────────────────────────────────────────────── */

function wire(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-modal]').forEach((b) => {
    b.addEventListener('click', () => openModal(b.dataset.modal!));
  });

  $<HTMLButtonElement>('pick-file').addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', async () => {
    if (!fileInput.files?.length) return;
    fromPaste = false;
    await ingest(await readFiles(Array.from(fileInput.files)));
  });

  const folderBtn = $<HTMLButtonElement>('pick-folder');
  if (supportsFolderPicker()) {
    folderInput.setAttribute('webkitdirectory', '');
    folderInput.setAttribute('multiple', '');
    folderBtn.addEventListener('click', () => folderInput.click());
    folderInput.addEventListener('change', async () => {
      if (!folderInput.files?.length) return;
      fromPaste = false;
      await ingest(await readFiles(Array.from(folderInput.files)));
    });
  } else {
    // Shipping a button that silently degrades to a single-file picker is worse than not shipping
    // it: the user believes they scanned a folder.
    folderBtn.remove();
    $<HTMLParagraphElement>('dz-hint').append(` ${copy.NO_FOLDER_ON_THIS_DEVICE}`);
  }

  for (const ev of ['dragenter', 'dragover'] as const) {
    dropzone.addEventListener(ev, (e) => {
      e.preventDefault();
      dropzone.classList.add('over');
    });
  }
  for (const ev of ['dragleave', 'drop'] as const) {
    dropzone.addEventListener(ev, () => dropzone.classList.remove('over'));
  }
  dropzone.addEventListener('drop', async (e) => {
    e.preventDefault();
    if (!e.dataTransfer) return;
    fromPaste = false;
    const files = await filesFromDataTransfer(e.dataTransfer);
    if (files.length) await ingest(await readFiles(files));
  });

  $<HTMLButtonElement>('try-sample').addEventListener('click', async (e) => {
    // stopPropagation so a sample control living inside the drop zone does not also open the file
    // picker behind it.
    e.stopPropagation();
    try {
      const res = await fetch('/samples/poisoned-readme.md');
      if (!res.ok) throw new Error(`sample returned ${res.status}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      fromPaste = false;
      log.emit('info', 'loaded the bundled sample');
      await ingest([{ name: 'poisoned-readme.md', bytes, route: 'text' }]);
    } catch (err) {
      showError(`The sample could not be loaded: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  const runPaste = async (): Promise<void> => {
    const text = pasteInput.value;
    if (!text.trim()) return;
    fromPaste = true;
    await ingest([{ name: '(pasted text)', bytes: encodePasted(text), route: 'text' }]);
  };
  pasteInput.addEventListener('paste', () => setTimeout(runPaste, 0));
  pasteInput.addEventListener('change', runPaste);

  $<HTMLButtonElement>('start-over').addEventListener('click', () => {
    pasteInput.value = '';
    reset();
    $<HTMLButtonElement>('start-over').setAttribute('hidden', '');
    inputNote.textContent = '';
  });

  $<HTMLButtonElement>('cancel').addEventListener('click', () => {
    cancelled = true;
    worker?.terminate();
    worker = null;
    progress.setAttribute('hidden', '');
    log.emit('warn', 'run cancelled');
  });

  $<HTMLButtonElement>('retry').addEventListener('click', () => clearError());

  profileSelect.addEventListener('change', () => {
    policy = { profile: profileSelect.value as Profile, overrides: {}, byCodePoint: new Map() };
    profileNote.textContent = copy.PROFILE_NOTES[policy.profile] ?? '';
    try {
      // Only the profile NAME and the theme are ever stored. The per-code-point overrides are not:
      // their KEYS would be the exact set of unusual code points found in a document the user
      // scanned precisely because they did not trust it, left in the browser profile for any later
      // same-origin script to read.
      localStorage.setItem('charsight:profile', policy.profile);
    } catch {
      /* storage disabled — the tool works without it */
    }
    renderLedger();
    renderExport();
  });

  filterSeverity.addEventListener('change', renderLedger);
  revealFile.addEventListener('change', renderReveal);
  revealDir.addEventListener('change', renderReveal);

  $<HTMLButtonElement>('dl-zip').addEventListener('click', () => {
    const a = artefacts();
    download('charsight-run.zip', a.zip.slice().buffer, 'application/zip');
    log.emit('good', 'wrote charsight-run.zip');
  });
  $<HTMLButtonElement>('dl-report').addEventListener('click', () => {
    download('charsight-report.html', artefacts().report, 'text/html');
  });
  $<HTMLButtonElement>('dl-csv').addEventListener('click', () => {
    download('findings.csv', artefacts().csv, 'text/csv');
  });
  $<HTMLButtonElement>('copy-cleaned').addEventListener('click', async () => {
    const first = prepared.cleaned[0];
    if (!first) return;
    await navigator.clipboard.writeText(new TextDecoder().decode(first.bytes));
    toast('Cleaned text copied.');
  });

  const share = $<HTMLButtonElement>('share');
  if (navigator.canShare?.({ files: [new File([new Uint8Array(1)], 'x.zip')] })) {
    share.hidden = false;
    share.addEventListener('click', async () => {
      const a = artefacts();
      const file = new File([new Uint8Array(a.zip)], 'charsight-run.zip', { type: 'application/zip' });
      try {
        await navigator.share({ files: [file], title: 'charsight run' });
      } catch {
        /* the user dismissed the sheet */
      }
    });
  }

  $<HTMLButtonElement>('log-copy').addEventListener('click', async () => {
    await navigator.clipboard.writeText(log.asText());
    toast('Event log copied.');
  });

  const themeBtn = $<HTMLButtonElement>('theme-toggle');
  const applyTheme = (t: string): void => {
    document.documentElement.dataset.theme = t;
    try {
      localStorage.setItem('charsight:theme', t);
    } catch {
      /* storage disabled */
    }
  };
  themeBtn.addEventListener('click', () => {
    applyTheme(document.documentElement.dataset.theme === 'light' ? 'dark' : 'light');
  });

  document.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement | null;
    const typing = target?.tagName === 'TEXTAREA' || target?.tagName === 'INPUT';
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void runPaste();
      return;
    }
    if (typing || e.metaKey || e.ctrlKey) return;
    if (e.key === '?') openModal('keys');
    else if (e.key.toLowerCase() === 'l') $<HTMLButtonElement>('log-toggle').click();
    else if (e.key.toLowerCase() === 'e') exportPanel.scrollIntoView({ behavior: 'smooth' });
  });
}

/* ── boot ───────────────────────────────────────────────────────────────────────────────────── */

paintCopy();
fillModalTemplates();
installGlossary();
wire();

try {
  const t = localStorage.getItem('charsight:theme');
  if (t) document.documentElement.dataset.theme = t;
  const p = localStorage.getItem('charsight:profile');
  if (p === 'report' || p === 'balanced' || p === 'strict') {
    policy = { profile: p, overrides: {}, byCodePoint: new Map() };
    profileSelect.value = p;
    profileNote.textContent = copy.PROFILE_NOTES[p] ?? '';
  }
} catch {
  /* storage disabled — the tool works without it */
}

log.emit('good', `charsight ready · Unicode ${UCD_VERSION} · no network request is made while it works`);
